import React, { useRef, useCallback } from "react";

import { toast } from "react-toastify";

import {
  isTeamFollowed,
  followTeam,
  unfollowTeam,
  setTeamTier,
  isPlayerFollowed,
  followPlayer,
  unfollowPlayer,
  setPlayerTier,
} from "../utils/sportsPreferences";

const LONG_PRESS_MS = 500;

const sizeClasses = {
  sm: "text-xs",
  md: "text-sm",
  lg: "text-base",
};

const iconSizes = {
  sm: "text-[11px]",
  md: "text-sm",
  lg: "text-base",
};

const FollowButton = ({
  type = "team",
  entity,
  size = "md",
  variant = "icon",
  showTier = false,
  onFollowChange,
  className = "",
}) => {
  const longPressTimer = useRef(null);
  const didLongPress = useRef(false);

  const entityName =
    type === "team"
      ? typeof entity === "string"
        ? entity
        : entity?.name
      : entity?.name || entity?.strPlayer;

  const entityId = type === "player" ? entity?.id || entity?.idPlayer : null;

  const getStatus = useCallback(() => {
    if (type === "team") {
      return isTeamFollowed(entityName);
    }

    return isPlayerFollowed(entityId, entityName);
  }, [type, entityName, entityId]);

  const status = getStatus();

  const handleFollow = () => {
    if (type === "team") {
      followTeam(entity, "following");
      toast.success(`Following ${entityName}`, {
        autoClose: 2000,
        icon: "💙",
      });
    } else {
      followPlayer(entity);
      toast.success(`Following ${entityName}`, {
        autoClose: 2000,
        icon: "💙",
      });
    }
    onFollowChange?.();
  };

  const handleUnfollow = () => {
    if (type === "team") {
      unfollowTeam(entityName);
    } else {
      unfollowPlayer(entityId, entityName);
    }
    toast.info(`Unfollowed ${entityName}`, { autoClose: 2000 });
    onFollowChange?.();
  };

  const handlePromoteToFavorite = () => {
    if (type === "team") {
      setTeamTier(entityName, "favorite");
    } else {
      const { key } = isPlayerFollowed(entityId, entityName);

      setPlayerTier(key, "favorite");
    }
    toast.success(`${entityName} added to Favorites`, {
      autoClose: 2000,
      icon: "⭐",
    });
    onFollowChange?.();
  };

  const handleDemoteFromFavorite = () => {
    if (type === "team") {
      setTeamTier(entityName, "following");
    } else {
      const { key } = isPlayerFollowed(entityId, entityName);

      setPlayerTier(key, "following");
    }
    toast.info(`${entityName} removed from Favorites`, { autoClose: 2000 });
    onFollowChange?.();
  };

  const onPointerDown = () => {
    didLongPress.current = false;
    if (!status.followed) return;

    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      if (status.tier === "favorite") {
        handleDemoteFromFavorite();
      } else {
        handlePromoteToFavorite();
      }
    }, LONG_PRESS_MS);
  };

  const onPointerUp = () => {
    clearTimeout(longPressTimer.current);
  };

  const onClick = (e) => {
    e.stopPropagation();
    if (didLongPress.current) {
      didLongPress.current = false;

      return;
    }
    if (status.followed) {
      handleUnfollow();
    } else {
      handleFollow();
    }
  };

  // Determine icon
  const isTeam = type === "team";
  let iconClass;

  if (!status.followed) {
    iconClass = isTeam ? "far fa-heart" : "far fa-user-plus";
  } else if (status.tier === "favorite") {
    iconClass = "fas fa-star";
  } else {
    iconClass = isTeam ? "fas fa-heart" : "fas fa-user-check";
  }

  // Determine color
  let colorClass;

  if (!status.followed) {
    colorClass = "text-white/20 hover:text-blue-400";
  } else if (status.tier === "favorite") {
    colorClass = "text-amber-400";
  } else {
    colorClass = "text-blue-400";
  }

  // ─── Variant: icon ─────────────────────────────────────
  if (variant === "icon") {
    return (
      <button
        className={`follow-heart transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:scale-105 rounded-full ${colorClass} ${iconSizes[size]} ${className}`}
        title={
          status.followed
            ? `${status.tier === "favorite" ? "★ " : ""}Following ${entityName} (hold to ${status.tier === "favorite" ? "demote" : "promote"})`
            : `Follow ${entityName}`
        }
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerLeave={onPointerUp}
        onPointerUp={onPointerUp}
      >
        <i className={iconClass} />
      </button>
    );
  }

  // ─── Variant: pill ─────────────────────────────────────
  if (variant === "pill") {
    let pillClasses;

    if (!status.followed) {
      pillClasses =
        "bg-white/5 border border-white/10 text-white/60 hover:bg-blue-500/15 hover:border-blue-500/30 hover:text-blue-300";
    } else if (status.tier === "favorite") {
      pillClasses = "bg-amber-500/20 border border-amber-500/40 text-amber-300";
    } else {
      pillClasses =
        "bg-blue-500/15 border border-blue-500/30 text-blue-300 hover:bg-red-500/15 hover:border-red-500/30 hover:text-red-300";
    }

    return (
      <button
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:scale-105 ${sizeClasses[size]} ${pillClasses} ${className}`}
        onClick={onClick}
        onPointerDown={onPointerDown}
        onPointerLeave={onPointerUp}
        onPointerUp={onPointerUp}
      >
        <i className={`${iconClass} ${iconSizes[size]}`} />
        {entityName}
        {status.followed && (
          <i className="fas fa-times text-[10px] opacity-50 group-hover:opacity-100" />
        )}
      </button>
    );
  }

  // ─── Variant: full ─────────────────────────────────────
  let fullClasses;

  if (!status.followed) {
    fullClasses =
      "bg-blue-500/20 text-blue-400 border border-blue-500/40 hover:bg-blue-500/30";
  } else if (status.tier === "favorite") {
    fullClasses =
      "bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30";
  } else {
    fullClasses =
      "bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/30";
  }

  let label;

  if (!status.followed) {
    label = isTeam ? "Follow Team" : "Follow Player";
  } else if (status.tier === "favorite") {
    label = "Favorite";
  } else {
    label = "Following";
  }

  return (
    <button
      className={`px-6 py-3 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:scale-105 ${sizeClasses[size]} ${fullClasses} ${className}`}
      onClick={onClick}
      onPointerDown={onPointerDown}
      onPointerLeave={onPointerUp}
      onPointerUp={onPointerUp}
    >
      <i className={iconClass} />
      {label}
      {showTier && status.followed && status.tier === "favorite" && (
        <i className="fas fa-star text-amber-400 text-[10px] ml-1" />
      )}
    </button>
  );
};

export default FollowButton;
